import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type ButtonVariant = 'pri' | 'acc' | 'sec' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * `<nb-button>` — the design-system button (Mockup `.btn` + variants).
 *
 * Renders a real `<button>` (or pass `type` for submit/reset) so semantics and
 * keyboard behaviour come for free. Project label text and optional `<nb-icon>`
 * children; icons inside are sized to 19px via `--nb-icon-size`.
 *
 * @example
 * <nb-button variant="pri" (click)="save()">Lagre</nb-button>
 * <nb-button variant="sec" size="sm"><nb-icon [icon]="Plus"/> Ny</nb-button>
 */
@Component({
  selector: 'nb-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.nb-btn-block]': 'block()',
  },
  template: `<button
    class="btn"
    [class]="classes()"
    [type]="type()"
    [disabled]="disabled()"
    [attr.aria-label]="ariaLabel() || null"
  >
    <ng-content />
  </button>`,
  styleUrl: './button.css',
})
export class NbButton {
  readonly variant = input<ButtonVariant>('pri');
  readonly size = input<ButtonSize>('md');
  readonly block = input(false);
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly disabled = input(false);
  /** Accessible name for icon-only usages (bound as `aria-label` on the inner button). */
  readonly ariaLabel = input<string | undefined>();

  protected readonly classes = computed(() => {
    const c: string[] = [this.variant()];
    if (this.size() !== 'md') c.push(this.size());
    if (this.block()) c.push('block');
    return c.join(' ');
  });
}
